import { useEffect, useContext } from 'react'
import { useToaster, Alert } from '@enterprise-ui/canvas-ui-react'
import { useAuth } from '@praxis/component-auth'
import { getLocation } from '@praxis/runtime-utils/getLocation'
import { useAnalytics } from '@praxis/analytics/client'
import { useNavigate, useSearchParams } from 'react-router'
import { SectionHeader } from '../../globalComponents/SectionHeader'
import {
  convertDateToLocalDateStringWithBackendFormatting,
  trainingLinkUrl,
} from '../../globalConfig/constants'
import { CostChangeInsightsPageContext } from './pageState'
import { CostChangeInsightsGlobalContext } from '../../globalState/CostChangeInsights'
import { VendorContext } from '../../globalState/Vendor/index'
import { CostChangeInsightsFilters } from './CostChangeInsightsFilters'
import {
  convertObjectToSearchParams,
  stripItemHubPath,
  generateCostLocationGroupsArray,
} from './helperFunctions'
import {
  COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
  COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP,
  COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
  COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
  COST_CHANGE_INSIGHTS_MISC_OPTIONS,
  TOASTER_DEFAULTS,
  CUSTOM_ANALYTICS_TYPES,
} from '../../globalConfig/constants'
import { CostChangeInsightsAppliedFilters } from './CostChangeInsightsAppliedFilters'
import { getCostChangeItems } from '../../services/requests'
import { CostChangeInsightsResults } from './CostChangeInsightsResults'
import isEmpty from 'lodash/isEmpty'
import usePrevious from '../../globalHooks/usePrevious'

export const CostChangeInsightsPage = ({
  highlightedFieldKeyNames,
  submenuText,
  submenuName,
}) => {
  const location = getLocation()
  const auth = useAuth()
  const userLanId = auth?.session?.userInfo?.lanId
  const { sendEvent } = useAnalytics()
  const navigate = useNavigate()
  const [pageState, pageDispatch] = useContext(CostChangeInsightsPageContext)
  const [globalState, globalDispatch] = useContext(
    CostChangeInsightsGlobalContext,
  )
  const [vendorState, vendorDispatch] = useContext(VendorContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const makeToast = useToaster()
  const pathname = stripItemHubPath(location.pathname)

  const ALL_SELECTION_FIELDS = {
    ...COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
    ...COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
    ...COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
    ...COST_CHANGE_INSIGHTS_MISC_OPTIONS,
  }

  const previousSearchCriteria = usePrevious(
    globalState?.[pathname].searchCriteria,
  )

  const dvsVendorState = vendorState.dvsStatus

  // This useEffect sets the global state default selections when mounting.
  // If params are given we'll use the completely empty default filters
  // object so that only the filters specified in the URL params will be
  // applied, and not a mix of URL params filter and global state filters.
  // If no URL params are detected then we'll use the default selections
  // object associated with the page/path
  useEffect(() => {
    const hasParamFilters = Array.from(searchParams.entries()).length
    if (!globalState?.[pathname].searchCriteria) {
      globalDispatch({
        type: 'set_default_selections',
        pathname: pathname,
        value: hasParamFilters
          ? COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS
          : COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP[
              pathname
            ].GET_DEFAULT_SELECTIONS(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // This useEffect handles updating the global and page state when mounting
  // with filters either from the URL params or in global state
  useEffect(() => {
    // We want to run this on mount, but only after the global state default
    // selections have been populated
    if (!previousSearchCriteria) {
      // If there's filters in the URL we'll use those to update the
      // corresponding global and page state, if not we'll use any existing
      // filters in global state to update the corresponding page state
      const hasParamFilters = Array.from(searchParams.entries()).length
      const globalStateSearchCriteria = globalState?.[pathname].searchCriteria

      const vendorIds = searchParams.getAll('VENDOR_IDS')
      if (vendorIds.length === 1) {
        if (vendorState.vendors[vendorIds[0]]) {
          vendorDispatch({
            type: 'update_selected_vendor',
            selectedVendor: vendorIds[0],
          })
        } else {
          navigate('/unauthorized-resource')
        }
      } else if (vendorIds.length !== 0) {
        navigate('/unauthorized-resource')
      }

      const autoSearch = searchParams.get('AUTO_SEARCH') === 'true'
      searchParams.delete('AUTO_SEARCH')
      searchParams.delete('VENDOR_IDS')

      if (
        !hasParamFilters &&
        isEmpty(convertObjectToSearchParams(globalStateSearchCriteria))
      ) {
        pageDispatch({
          type: 'reset_to_initial_state',
        })
      } else {
        const paramSearchObj = !hasParamFilters
          ? convertObjectToSearchParams(globalStateSearchCriteria)
          : Array.from(searchParams.entries()).reduce(
              // eslint-disable-next-line no-sequences
              (acc, a) => ((acc[a[0]] = acc[a[0]] || []).push(a[1]), acc),
              {},
            )
        Object.keys(paramSearchObj).forEach((key) => {
          // Filters from the URL will always be in an array, so we'll do the
          // same if they come from global state so that we can use the code
          // below for both purposes
          const convertedParamValue = !hasParamFilters
            ? [paramSearchObj[key]].flat()
            : paramSearchObj[key]
          const paramValue = Object.keys(
            COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
          ).includes(key)
            ? [
                {
                  startDate: convertedParamValue[0].split('-')[0],
                  endDate: convertedParamValue[0].split('-')[1],
                },
              ]
            : convertedParamValue
          const globalValueToUpdate = Array.isArray(
            COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS[
              ALL_SELECTION_FIELDS[key]?.variableName
            ],
          )
            ? paramValue
            : paramValue[0]
          const pageValueToUpdate = Array.isArray(
            COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS[
              ALL_SELECTION_FIELDS[key]?.variableName
            ],
          )
            ? paramValue.map((str) => ({
                id: str,
                value: str,
                label: str,
              }))
            : paramValue[0]
          globalDispatch({
            type: 'update_criteria_by_field',
            field: ALL_SELECTION_FIELDS[key],
            value: globalValueToUpdate,
            pathname: pathname,
          })
          if (!Object.keys(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).includes(key)) {
            pageDispatch({
              type: 'update_value_states_by_field',
              field: ALL_SELECTION_FIELDS[key],
              value: pageValueToUpdate,
            })
          }
        })
      }
      if (autoSearch) {
        globalDispatch({
          type: 'initiate_search',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState?.[pathname].searchCriteria])

  useEffect(() => {
    setSearchParams(
      convertObjectToSearchParams(globalState?.[pathname].searchCriteria),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState?.[pathname].searchCriteria])

  useEffect(() => {
    if (pageState.initiateSearch || globalState.initiateSearch) {
      makeCostChangeItemsRequest(true)
      globalDispatch({
        type: 'reset_initiate_search',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.initiateSearch, globalState.initiateSearch])

  useEffect(() => {
    if (
      pageState.currentPage &&
      pageState.recordsPerPage &&
      pageState.costChangeItems &&
      pageState.searchAfterPaginationChange
    ) {
      makeCostChangeItemsRequest()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.currentPage, pageState.recordsPerPage])

  useEffect(() => {
    sendEvent({
      event: {
        type: CUSTOM_ANALYTICS_TYPES.PAGE_VIEW,
        id: 'Insights Page',
      },
      client: {
        employee_login_id: userLanId,
      },
    })
  }, [sendEvent, userLanId])

  useEffect(() => {
    if (pageState.costChangeItems) {
      pageDispatch({ type: 'clear_cost_change_items' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorState.selectedVendor])

  const sendFailureToast = () => {
    makeToast({
      ...TOASTER_DEFAULTS,
      type: 'error',
      heading: 'Error',
      message: 'Failed to get search results',
    })
  }

  const makeCostChangeItemsRequest = (resetToFirstPage) => {
    const requestBody = {
      ...globalState?.[pathname].searchCriteria,
      costLocationGroups: generateCostLocationGroupsArray(
        globalState?.[pathname].searchCriteria,
        dvsVendorState,
        vendorState.selectedVendor,
      ),
      page: resetToFirstPage ? 1 : pageState.currentPage,
      vendorIds: [vendorState.selectedVendor],
      pageSize: pageState.recordsPerPage || 10,
    }
    delete requestBody[
      COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
        .variableName
    ]
    delete requestBody[
      COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
        .variableName
    ]
    Object.values(COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS).forEach((option) => {
      if (requestBody[option.variableName]) {
        requestBody[option.variableName] = {
          startDate: convertDateToLocalDateStringWithBackendFormatting(
            new Date(requestBody[option.variableName].startDate),
          ),
          endDate: convertDateToLocalDateStringWithBackendFormatting(
            new Date(requestBody[option.variableName].endDate),
          ),
        }
      }
    })

    pageDispatch({ type: 'flip_get_cost_change_items_loading_status' })
    getCostChangeItems(requestBody)
      .then((response) => {
        if (response.status === 200) {
          pageDispatch({
            type: 'set_cost_change_items',
            value: {
              items: response.data,
              totalResults: Number(response?.headers?.['result-count']),
            },
          })
          pageDispatch({
            type: 'set_cost_change_items_request_criteria',
            value: globalState?.[pathname].searchCriteria,
          })
          if (resetToFirstPage) {
            pageDispatch({ type: 'reset_current_page' })
          }
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        sendFailureToast()
        pageDispatch({
          type: 'set_cost_change_items',
          value: null,
        })
        pageDispatch({ type: 'reset_current_page' })
      })
      .finally(() => {
        pageDispatch({ type: 'flip_get_cost_change_items_loading_status' })
      })
  }

  const onSearchClick = () => {
    makeCostChangeItemsRequest(true)
  }

  const headerSubtext = (
    <>
      <span>
        {`Use this page to gain insights into all costs that have been 
        submitted for your currently represented vendor. Visit our `}
      </span>
      <a
        href={trainingLinkUrl}
        target="_blank"
        className="hesp-link"
        rel="noreferrer"
      >
        training materials
      </a>
      <span>
        {` to learn more about how to most effectively use this page.`}
      </span>
      {submenuText && <Alert className="hc-mt-normal">{submenuText}</Alert>}
    </>
  )

  return (
    <>
      <SectionHeader
        className="hesp-sectionHeader-layout"
        pageHeading={`Insights${submenuName ? ` - ${submenuName}` : ''}`}
        subtext={headerSubtext}
      />
      <CostChangeInsightsFilters
        onSearchClick={onSearchClick}
        highlightedFieldKeyNames={highlightedFieldKeyNames}
      />
      <CostChangeInsightsAppliedFilters />
      <CostChangeInsightsResults />
    </>
  )
}
